import { isPlatformBrowser } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { gql } from "apollo-angular";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { DataService } from "../../providers/data/data.service";
import { ExportDownloadService } from "../../providers/export-download/export-download.service";
import { NotificationService } from "../../providers/notification/notification.service";
import { GET_ACTIVE_CUSTOMER } from "../product-list/product-list.graphql";
import { formatLeadTimeWeeksLabel, resolveEffectiveLeadTimeWeeks } from "../../../shared/utils/lead-time-weeks.util";
import { MOV_FILTER_OPTIONS, LEAD_TIME_FILTER_OPTIONS } from "../../../shared/constants/pricelist.constants";

interface LimitedOffer {
    pricelist: { id: string; channelCode: string | null; mov: number | null; leadTimeFrom: number | null; leadTimeTo: number | null; validTo: string | null };
    title: string;
    createdAt: string;
    brands: string[];
    skuCount: number;
    retailDiscountPercent: number | null;
    takeAllOnly: boolean;
    clearance: boolean;
    damagedPackaging: boolean;
    shortBbd: boolean;
}

const GET_LIMITED_OFFERS = gql`
    query GetSpecialLimitedOffers {
        specialLimitedOffers {
            totalSkuCount
            items {
                pricelist { id channelCode mov leadTimeFrom leadTimeTo validTo }
                title createdAt brands skuCount retailDiscountPercent
                takeAllOnly clearance damagedPackaging shortBbd
            }
        }
    }
`;

@Component({
    selector: "vsf-limited-offers",
    templateUrl: "./limited-offers.component.html",
    styleUrls: ["./limited-offers.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LimitedOffersComponent implements OnInit, OnDestroy {
    @ViewChild("filterPanel") filterPanel: ElementRef<HTMLElement>;
    loading = true;
    loadError = false;
    signedIn = false;
    offers: LimitedOffer[] = [];
    filteredOffers: LimitedOffer[] = [];
    totalSkus = 0;
    brandOptions: Array<{ name: string; count: number }> = [];
    selectedBrands = new Set<string>();
    brandSearch = "";
    showAllBrands = false;
    expandedOfferBrands = new Set<string>();
    mov = "";
    eta = "";
    sort = "newest";
    page = 1;
    mobileLimit = 3;
    filtersOpen = false;
    sidebarCollapsed = false;
    busyDownloads = new Set<string>();
    readonly movOptions = MOV_FILTER_OPTIONS;
    readonly etaOptions = LEAD_TIME_FILTER_OPTIONS;
    private destroyed$ = new Subject<void>();
    private previousFocus: HTMLElement | null = null;
    private refreshTimer: ReturnType<typeof setInterval>;
    private now = Date.now();

    constructor(private data: DataService, private downloads: ExportDownloadService,
        private notifications: NotificationService, private router: Router, private cd: ChangeDetectorRef,
        private zone: NgZone, @Inject(PLATFORM_ID) private platformId: object) {}

    ngOnInit(): void {
        this.reloadCustomer();
        if (isPlatformBrowser(this.platformId)) {
            this.zone.runOutsideAngular(() => {
                this.refreshTimer = setInterval(() => this.zone.run(() => {
                    this.applyFilters(false);
                    if (this.signedIn) this.load(true);
                }), 60_000);
            });
        }
    }
    reloadCustomer(): void {
        this.loading = true; this.loadError = false;
        this.data.query<{activeCustomer?: {id: string} | null}>(GET_ACTIVE_CUSTOMER, {}, "network-only")
            .pipe(take(1), takeUntil(this.destroyed$)).subscribe({
                next: result => { this.signedIn = !!result.activeCustomer; this.signedIn ? this.load() : this.finishLoading(); },
                error: () => { this.loadError = true; this.finishLoading(); },
            });
    }
    ngOnDestroy(): void { this.destroyed$.next(); this.destroyed$.complete(); clearInterval(this.refreshTimer); }
    private finishLoading(): void { this.loading = false; this.cd.markForCheck(); }
    load(background = false): void {
        if (!background) { this.loading = true; this.loadError = false; }
        this.data.query<{specialLimitedOffers: {items: LimitedOffer[]; totalSkuCount: number}}>(GET_LIMITED_OFFERS, {}, "network-only")
            .pipe(take(1), takeUntil(this.destroyed$)).subscribe({
                next: ({specialLimitedOffers}) => {
                    this.offers = specialLimitedOffers.items; this.totalSkus = specialLimitedOffers.totalSkuCount;
                    const brands = new Map<string, {name: string; count: number}>();
                    this.offers.forEach(offer => new Set(offer.brands.map(name => name.toLocaleLowerCase())).forEach(key => {
                        const name = offer.brands.find(brand => brand.toLocaleLowerCase() === key)!;
                        const previous = brands.get(key); brands.set(key, {name: previous?.name ?? name, count: (previous?.count ?? 0) + 1});
                    }));
                    this.brandOptions = [...brands.values()].sort((a,b) => a.name.localeCompare(b.name));
                    this.loadError = false; this.applyFilters(!background); this.finishLoading();
                },
                error: () => { if (!background) { this.loadError = true; this.finishLoading(); } },
            });
    }
    get liveOffers(): LimitedOffer[] { return this.offers.filter(offer => !offer.pricelist.validTo || Date.parse(offer.pricelist.validTo) > this.now); }
    get sidebarBrands(): Array<{name: string; count: number}> {
        const brands = this.brandOptions.filter(brand => brand.name.toLocaleLowerCase().includes(this.brandSearch.trim().toLocaleLowerCase()));
        return this.showAllBrands || this.brandSearch ? brands : brands.slice(0,5);
    }
    get chips(): Array<{id: string; label: string}> {
        return [...[...this.selectedBrands].map(brand => ({id:`brand:${brand}`,label:brand})),
            ...(this.mov ? [{id:'mov',label:`MOV: ${this.movOptions.find(option => option.id === this.mov)?.label}`}] : []),
            ...(this.eta ? [{id:'eta',label:`ETA: ${this.etaOptions.find(option => option.id === this.eta)?.label}`}] : [])];
    }
    get pageCount(): number { return Math.max(1,Math.ceil(this.filteredOffers.length / 6)); }
    get pageOffers(): LimitedOffer[] { return this.filteredOffers.slice((this.page-1)*6,this.page*6); }
    get mobileOffers(): LimitedOffer[] { return this.filteredOffers.slice(0,this.mobileLimit); }
    get pageStart(): number { return this.filteredOffers.length ? (this.page-1)*6+1 : 0; }
    get pageEnd(): number { return Math.min(this.page*6,this.filteredOffers.length); }
    get pages(): number[] {
        const start = Math.max(1,Math.min(this.page-1,this.pageCount-2));
        return Array.from({length:Math.min(3,this.pageCount)},(_,i) => start+i);
    }
    offerBrandLabel(offer: LimitedOffer): string {
        const brands = this.expandedOfferBrands.has(offer.pricelist.id) ? offer.brands : offer.brands.slice(0, 3);
        return brands.join(', ') || '—';
    }
    toggleOfferBrands(id: string): void {
        this.expandedOfferBrands.has(id) ? this.expandedOfferBrands.delete(id) : this.expandedOfferBrands.add(id);
    }
    toggleBrand(brand: string): void { this.selectedBrands.has(brand) ? this.selectedBrands.delete(brand) : this.selectedBrands.add(brand); this.applyFilters(); }
    removeChip(id: string): void { if(id === 'mov') this.mov=''; else if(id === 'eta') this.eta=''; else this.selectedBrands.delete(id.slice(6)); this.applyFilters(); }
    clear(): void { this.selectedBrands.clear(); this.mov=''; this.eta=''; this.brandSearch=''; this.applyFilters(); }
    applyFilters(resetPage = true): void {
        this.now = Date.now();
        const mov = this.movOptions.find(option => option.id === this.mov);
        const eta = this.etaOptions.find(option => option.id === this.eta);
        this.filteredOffers = this.liveOffers.filter(offer => {
            const weeks = this.weeks(offer);
            return (!this.selectedBrands.size || offer.brands.some(brand => [...this.selectedBrands].some(selected => selected.toLocaleLowerCase() === brand.toLocaleLowerCase())))
                && (!mov || (offer.pricelist.mov != null && offer.pricelist.mov >= mov.min && offer.pricelist.mov <= mov.max))
                && (!eta || (weeks != null && (eta.min == null || weeks >= eta.min) && (eta.max == null || weeks <= eta.max)));
        }).sort((a,b) => {
            if(this.sort === 'expiry') return (a.pricelist.validTo ? Date.parse(a.pricelist.validTo) : Infinity) - (b.pricelist.validTo ? Date.parse(b.pricelist.validTo) : Infinity);
            if(this.sort === 'retail') return (b.retailDiscountPercent ?? -1) - (a.retailDiscountPercent ?? -1);
            if(this.sort === 'eta') return (this.weeks(a) ?? Infinity) - (this.weeks(b) ?? Infinity);
            return Date.parse(b.createdAt)-Date.parse(a.createdAt) || Number(b.pricelist.id)-Number(a.pricelist.id);
        });
        if(resetPage) { this.page=1; this.mobileLimit=3; }
        this.page=Math.min(this.page,this.pageCount); this.cd.markForCheck();
    }
    changePage(page: number): void { this.page=Math.max(1,Math.min(page,this.pageCount)); }
    weeks(offer: LimitedOffer): number | null { return resolveEffectiveLeadTimeWeeks(offer.pricelist.leadTimeFrom,offer.pricelist.leadTimeTo); }
    etaLabel(offer: LimitedOffer): string { return formatLeadTimeWeeksLabel(offer.pricelist.leadTimeFrom,offer.pricelist.leadTimeTo) || 'Not specified'; }
    fastest(offer: LimitedOffer): boolean {
        const known = this.filteredOffers.map(item => this.weeks(item)).filter((weeks): weeks is number => weeks != null && weeks > 0);
        return known.length > 1 && this.weeks(offer) === Math.min(...known) && Math.min(...known) < Math.max(...known);
    }
    expiryLabel(offer: LimitedOffer, mobile=false): string {
        if(!offer.pricelist.validTo) return 'While stock lasts';
        const days=Math.max(0,Math.ceil((Date.parse(offer.pricelist.validTo)-this.now)/86_400_000));
        return `${mobile ? 'Expires in' : 'In'} ${days} ${days === 1 ? 'day' : 'days'}`;
    }
    expiryHint(offer: LimitedOffer): string { return offer.pricelist.validTo ? `Expires ${new Date(offer.pricelist.validTo).toLocaleString()}, or when stock runs out — whichever comes first.` : 'Available while the listed stock lasts.'; }
    view(offer: LimitedOffer): void { this.router.navigate(['/category/products',offer.pricelist.id]); }
    signIn(): void { this.router.navigate(['/account/sign-in'],{queryParams:{returnUrl:'/pricelists/short-offer'}}); }
    async download(offer?: LimitedOffer): Promise<void> {
        const id=offer?.pricelist.id ?? 'catalog'; if(this.busyDownloads.has(id)) return;
        this.busyDownloads.add(id);
        try {
            if(offer) await this.downloads.downloadSupplierShareable(id,`${offer.title} ${id}`,'xlsx');
            else await this.downloads.downloadCatalogShareable('short-offer','Venduro Limited Offers','xlsx',{
                supplierIds:this.filteredOffers.map(item => item.pricelist.id),
            });
        } catch { this.notifications.error('Unable to download this offer. Please try again.').subscribe(); }
        finally { this.busyDownloads.delete(id); this.cd.markForCheck(); }
    }
    openFilters(): void {
        this.previousFocus=document.activeElement as HTMLElement; this.filtersOpen=true;
        setTimeout(() => this.filterPanel.nativeElement.querySelector<HTMLButtonElement>('.filters-close')?.focus());
    }
    closeFilters(): void { this.filtersOpen=false; this.previousFocus?.focus(); }
    @HostListener('document:keydown.escape') escape(): void { if(this.filtersOpen) this.closeFilters(); }
    trapFocus(event: KeyboardEvent): void {
        if(!this.filtersOpen || event.key !== 'Tab') return;
        const items=Array.from(this.filterPanel.nativeElement.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"], a[href]')).filter(item => item.offsetParent !== null);
        const first=items[0],last=items[items.length-1];
        if(event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if(!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    trackOffer(_index: number, offer: LimitedOffer): string { return offer.pricelist.id; }
}
